import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSearch, Download, AlertTriangle, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: false });

/**
 * COA (Certificate of Analysis) lookup card.
 *
 * Used in TWO places — exactly the same component, no auth required either way:
 *   1) Public `/coa` page (linked from marketing site / vial QR codes)
 *   2) Inside the patient companion app dashboard
 *
 * Flow:
 *   - Patient types lot number → POST /coa/search?lot=...
 *   - If 1 match → render "Download COA" CTA
 *   - If >1 match with different pharmacies → show a disambiguation
 *     dropdown ("Which pharmacy did your vial come from?")
 *   - If 0 matches → friendly empty state suggesting they ask the bot
 *
 * NO PHI involved — only public regulatory document lookups.
 */
export default function COASearchCard({ embedded = false }) {
  const [lot, setLot] = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [needsPharmacy, setNeedsPharmacy] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");

  const reset = () => { setMatches(null); setNeedsPharmacy(false); setSelectedPharmacy(""); };

  const doSearch = async (pharmacyOverride) => {
    if (lot.trim().length < 3) return;
    setSearching(true);
    try {
      const params = { lot: lot.trim() };
      const phid = pharmacyOverride ?? (needsPharmacy ? selectedPharmacy : null);
      if (phid) params.pharmacy_id = phid;
      const { data } = await PUBLIC.get("/coa/search", { params });
      setMatches(data.matches || []);
      setNeedsPharmacy(!!data.needs_pharmacy_disambiguation && !phid);
    } catch {
      setMatches([]);
      setNeedsPharmacy(false);
    } finally {
      setSearching(false);
    }
  };

  const downloadCoa = (coaId, lotNum) => {
    const url = `${BACKEND_URL}/api/coa/${coaId}/file`;
    // Open in new tab so the patient gets the inline PDF viewer and can
    // also Save-As to email/text it from their device.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Wrapper class differs by context: embedded inside the dashboard gets
  // tighter padding; standalone (public /coa page) gets generous spacing.
  const wrap = embedded
    ? "rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
    : "rounded-2xl border border-slate-200 bg-white p-8 shadow-sm";

  return (
    <div className={wrap} data-testid="coa-search-card">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-emerald-500" />
        <h3 className={embedded ? "font-semibold text-slate-900 dark:text-slate-100" : "text-xl font-bold text-slate-900"}>
          Look up your COA (Certificate of Analysis)
        </h3>
      </div>
      <p className={`${embedded ? "text-xs" : "text-sm"} text-slate-600 dark:text-slate-400 mb-4`}>
        Find the lab-tested certificate for the exact vial you received. Type the lot number printed on your vial below.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          <Label htmlFor="coa-lot-input" className="text-xs">Lot number</Label>
          <Input
            id="coa-lot-input"
            value={lot}
            onChange={(e) => { setLot(e.target.value); reset(); }}
            placeholder="e.g. LOT-12345"
            className="mt-1 h-11 rounded-full"
            onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
            data-testid="coa-lot-input"
          />
        </div>
        <Button
          onClick={() => doSearch()}
          disabled={searching || lot.trim().length < 3}
          className="h-11 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold whitespace-nowrap flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="coa-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
          <span>Find COA</span>
        </Button>
      </div>

      {/* Disambiguation — same lot number was uploaded by multiple pharmacies */}
      {needsPharmacy && matches && matches.length > 1 && (
        <div className="mt-4 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/60" data-testid="coa-pharmacy-picker">
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>This lot number matches <b>{matches.length}</b> COAs across different pharmacies. Please select the pharmacy your vial came from:</span>
          </p>
          <Select
            value={selectedPharmacy}
            onValueChange={(v) => { setSelectedPharmacy(v); doSearch(v); }}
          >
            <SelectTrigger className="bg-white" data-testid="coa-pharmacy-select">
              <SelectValue placeholder="Select pharmacy" />
            </SelectTrigger>
            <SelectContent>
              {[...new Map(matches.map((m) => [m.pharmacy_id, m])).values()].map((m) => (
                <SelectItem key={m.pharmacy_id} value={m.pharmacy_id}>{m.pharmacy_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Results */}
      {matches !== null && !needsPharmacy && (
        <div className="mt-4 space-y-2" data-testid="coa-results">
          {matches.length === 0 && (
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">No COA found for lot <b className="font-mono">{lot}</b>.</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Double-check the lot number on your vial. Still stuck? Open the chat bot and ask &ldquo;Find COA for lot {lot}&rdquo; — our team can help.
              </p>
            </div>
          )}
          {matches.map((m) => (
            <div
              key={m.id}
              className="p-3 rounded-lg border border-emerald-300 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`coa-match-${m.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">{m.medication_name}</p>
                  <span className="text-[10px] font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">{m.lot_number}</span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  {m.pharmacy_name}
                  {/* Only render an expiration when it's a valid, future-dated ISO
                      value. Past-dated or unparseable strings suggest the pharmacy
                      typed the wrong date in the source COA — showing "Expires
                      2026-06-08" (already-past) on a patient-facing page looks
                      broken, so we hide it entirely and let the admin correct it
                      via the needs_review flow. */}
                  {(() => {
                    const raw = m.expiration_date;
                    if (!raw) return "";
                    const parsed = new Date(raw);
                    if (isNaN(parsed.getTime())) return "";
                    // Compare against midnight today to avoid tz-edge false-negatives.
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (parsed < today) return "";
                    return ` · Expires ${raw}`;
                  })()}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => downloadCoa(m.id, m.lot_number)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid={`coa-download-${m.id}`}
              >
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
